import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useGroupStore } from '@/stores/groupStore';
import {
  LayoutTemplate,
  Save,
  Copy,
  Download,
  FolderTree,
  ChevronDown,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';

interface SelectionToolbarProps {
  selectedNodeIds: string[];
  style?: React.CSSProperties;
  selectionBox?: { x: number; y: number; width: number; height: number };
}

interface ToolbarButton {
  id: string;
  type: 'button';
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}

interface ToolbarSeparator {
  id: string;
  type: 'separator';
}

interface ToolbarDropdown {
  id: string;
  type: 'dropdown';
  icon: React.ElementType;
  label: string;
  items: { icon: React.ElementType; label: string; onClick: () => void }[];
}

type ToolbarItem = ToolbarButton | ToolbarSeparator | ToolbarDropdown;

const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  useEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return { open, setOpen, triggerRef, dropdownRef, pos };
};

export const SelectionToolbar: React.FC<SelectionToolbarProps> = memo(({
  selectedNodeIds,
  style,
  selectionBox,
}) => {
  const createGroup = useGroupStore((s) => s.createGroup);
  const groupDropdown = useDropdown();

  const handleCreateGroup = () => {
    if (selectedNodeIds.length >= 2) {
      createGroup(selectedNodeIds, selectionBox);
      toast.success('打组成功', { duration: 2000 });
    } else {
      toast.warning('请至少选择2个节点进行打组', { duration: 2000 });
    }
    groupDropdown.setOpen(false);
  };

  const items: ToolbarItem[] = [
    { id: 'arrange', type: 'button', icon: LayoutTemplate, label: '排列', onClick: () => toast.info('排列功能开发中，敬请期待', { duration: 2000 }) },
    { id: 'sep-1', type: 'separator' },
    { id: 'save', type: 'button', icon: Save, label: '保存到素材', onClick: () => toast.info('保存到素材功能开发中，敬请期待', { duration: 2000 }) },
    { id: 'copy', type: 'button', icon: Copy, label: '创建副本', onClick: () => toast.info('创建副本功能开发中，敬请期待', { duration: 2000 }) },
    { id: 'download', type: 'button', icon: Download, label: '批量下载', onClick: () => toast.info('批量下载功能开发中，敬请期待', { duration: 2000 }) },
    { id: 'sep-2', type: 'separator' },
    {
      id: 'group',
      type: 'dropdown',
      icon: FolderTree,
      label: '打组',
      items: [
        { icon: FolderTree, label: '打组', onClick: handleCreateGroup },
        { icon: Layers, label: '合并分镜组', onClick: () => { toast.info('合并分镜组功能开发中，敬请期待', { duration: 2000 }); groupDropdown.setOpen(false); } },
      ],
    },
  ];

  return (
    <div
      className="flex items-center gap-1 pointer-events-auto bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg"
      style={style}
    >
      {items.map((item) => {
        if (item.type === 'separator') {
          return <div key={item.id} className="w-px h-5 bg-white/10 mx-1" />;
        }

        if ('items' in item) {
          const dropdown = groupDropdown;
          const isOpen = dropdown.open;
          return (
            <div key={item.id} className="relative">
              <button
                ref={dropdown.triggerRef}
                onClick={() => dropdown.setOpen(!isOpen)}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex items-center gap-[6px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
                title={item.label}
              >
                <item.icon size={16} />
                <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && createPortal(
                <div
                  ref={dropdown.dropdownRef}
                  className="fixed bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg overflow-hidden z-[9999] min-w-[140px]"
                  style={{ top: dropdown.pos.top, left: dropdown.pos.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.items.map((sub) => (
                    <button
                      key={sub.label}
                      onClick={sub.onClick}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:text-white hover:bg-white/10 transition-all text-left"
                    >
                      <sub.icon size={14} />
                      <span>{sub.label}</span>
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          );
        }

        return (
          <button
            key={item.id}
            onClick={item.onClick}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center gap-[6px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
            title={item.label}
          >
            <item.icon size={16} />
            <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
});

SelectionToolbar.displayName = 'SelectionToolbar';
